import { c as _c } from "react/compiler-runtime";
import { noop } from 'betterplace-utils';
import classNames from 'classnames';
import { DonationFeedItem, Icon, LayoutStack, type RowRange, VirtualScroll, VirtualScrollItemStyle, useIsMounted } from 'design-system/client';
import { useTranslations } from 'next-intl';
import { type CSSProperties, ForwardedRef, forwardRef, useCallback, useMemo, useRef } from 'react';
import type { FEPPContentDonation } from '../../../types';
import DonationCard, { type DonationCardProps } from '../DonationCard';
import styles from './Donations.module.css';
import { useProgressAndDonationsContext } from '@/fundraisingEventPage/_dependencies/components/FundraisingEventPageContent/ProgressAndDonations/ProgressAndDonationsContext';
const VISIBLE_ROW_COUNT = 6;
function StaticDonationFeed(t0) {
  const $ = _c(9);
  const {
    data
  } = t0;
  const donationCount = data.length;
  let t1;
  if ($[0] !== data) {
    t1 = data.slice(0, VISIBLE_ROW_COUNT);
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const donations = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <NewDonationsIndicator onClick={noop} row={0} newDonationsCount={0} />;
    t3 = classNames(styles.feedContainer, styles.onTop, styles.onBottom);
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== donationCount || $[5] !== donations) {
    t4 = !!donationCount && donations.map(_temp);
    $[4] = donationCount;
    $[5] = donations;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <>{t2}<div className={t3}><LayoutStack space="100" className={styles.staticFeedContainer}>{t4}</LayoutStack></div></>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function _temp(donation) {
  return donation && <DonationCard key={donation.id} donation={donation} />;
}
function useDonationCardProps(t0) {
  const $ = _c(4);
  const {
    index
  } = t0;
  const {
    donations,
    offset,
    row
  } = useProgressAndDonationsContext();
  const item = donations[index - offset] ?? undefined;
  const t1 = row === 0;
  let t2;
  if ($[0] !== index || $[1] !== item || $[2] !== t1) {
    t2 = {
      donation: item,
      stableKey: index,
      mayStartAnimation: t1
    };
    $[0] = index;
    $[1] = item;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const donationCardProps = t2;
  return donationCardProps;
}
const DonationFeedCard = forwardRef(function DonationFeedCard(t0, ref) {
  const $ = _c(7);
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = style;
  } else {
    props = $[1];
    style = $[2];
  }
  const donationCardProps = useDonationCardProps(props);
  let t1;
  if ($[3] !== donationCardProps || $[4] !== ref || $[5] !== style) {
    t1 = <DonationCard ref={ref} {...donationCardProps} style={style} />;
    $[3] = donationCardProps;
    $[4] = ref;
    $[5] = style;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
});
function NewDonationsIndicator({
  onClick,
  row,
  newDonationsCount
}: {
  onClick: () => void;
  row: number;
  newDonationsCount: number;
}) {
  const t = useTranslations('nextjs.donation_feed');
  const shouldShowIndicator = row !== 0 && !!newDonationsCount;
  return <div className={classNames(styles.newDonationsContainer, {
    [styles.noNewDonations!]: !shouldShowIndicator
  })}>
      {shouldShowIndicator && <button className={styles.newDonationsButton} onClick={onClick}>
          <div className={styles.newDonationsContent}>
            <span className={styles.newDonationsText}>{t('view_new_donations', {
            count: newDonationsCount
          })}</span>
            <div className={styles.iconContainer}>
              <Icon name="arrowUp" className={styles.newDonationsIcon} color="fg-light-primary" />
            </div>
          </div>
        </button>}
    </div>;
}
const reactiveProps: {
  style: CSSProperties;
} = {
  style: {
    border: 0,
    borderRadius: 'var(--betterplace-border-radius-300)',
    backgroundColor: 'var(--betterplace-color-bg-light)'
  }
};
function ScrollableDonationFeed({
  maxRows,
  onScroll,
  row,
  visibleRowCount,
  initialDonationLimit,
  donationCount
}: {
  fundraisingEventId: string;
  maxRows: number;
  onScroll: (range: RowRange) => void;
  row: number;
  visibleRowCount: number;
  initialDonationLimit: number;
  donationCount: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    newDonationsOffset,
    requestedRow
  } = useProgressAndDonationsContext();
  const scrollToTop = useCallback(() => {
    ref.current?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  const isOnBottom = donationCount && requestedRow >= donationCount - VISIBLE_ROW_COUNT;
  return <>
      <NewDonationsIndicator newDonationsCount={newDonationsOffset} onClick={scrollToTop} row={row} />
      <div className={classNames(styles.feedContainer, {
      [styles.onTop!]: !requestedRow,
      [styles.onBottom!]: isOnBottom
    })}>
        <VirtualScroll ref={ref} visibleRowCount={visibleRowCount} gap={4} overscanRows={initialDonationLimit} maxRows={maxRows} onScroll={onScroll} className={styles.scrollContainer} offset={row ? newDonationsOffset : 0} debug={false} reactiveProps={reactiveProps}>
          {DonationFeedCard}
        </VirtualScroll>
      </div>
    </>;
}
type DonationsProps = {
  fundraisingEventId: string;
  maxRows: number;
  receivedDonationsCount: number;
  initialDonations: Array<FEPPContentDonation>;
  initialDonationLimit: number;
  donationCount: number;
};
function DonationsCombined(props) {
  const $ = _c(18);
  const mounted = useIsMounted();
  let donationCount;
  let fundraisingEventId;
  let initialDonationLimit;
  let initialDonations;
  let maxRows;
  let rest;
  if ($[0] !== props) {
    ({
      maxRows,
      fundraisingEventId,
      initialDonationLimit,
      donationCount,
      initialDonations,
      ...rest
    } = props);
    $[0] = props;
    $[1] = donationCount;
    $[2] = fundraisingEventId;
    $[3] = initialDonationLimit;
    $[4] = initialDonations;
    $[5] = maxRows;
    $[6] = rest;
  } else {
    donationCount = $[1];
    fundraisingEventId = $[2];
    initialDonationLimit = $[3];
    initialDonations = $[4];
    maxRows = $[5];
    rest = $[6];
  }
  const {
    onScroll,
    row,
    donations
  } = useProgressAndDonationsContext();
  const visibleRowCount = Math.min(VISIBLE_ROW_COUNT, donationCount);
  if (mounted && donations.length) {
    let t0;
    if ($[7] !== donationCount || $[8] !== fundraisingEventId || $[9] !== initialDonationLimit || $[10] !== maxRows || $[11] !== onScroll || $[12] !== row || $[13] !== visibleRowCount) {
      t0 = <ScrollableDonationFeed maxRows={maxRows} fundraisingEventId={fundraisingEventId} onScroll={onScroll} row={row} visibleRowCount={visibleRowCount} initialDonationLimit={initialDonationLimit} donationCount={donationCount} />;
      $[7] = donationCount;
      $[8] = fundraisingEventId;
      $[9] = initialDonationLimit;
      $[10] = maxRows;
      $[11] = onScroll;
      $[12] = row;
      $[13] = visibleRowCount;
      $[14] = t0;
    } else {
      t0 = $[14];
    }
    return t0;
  }
  const data = donations.length ? donations : initialDonations;
  let t0;
  if ($[15] !== data || $[16] !== rest) {
    t0 = <StaticDonationFeed {...rest} data={data} />;
    $[15] = data;
    $[16] = rest;
    $[17] = t0;
  } else {
    t0 = $[17];
  }
  return t0;
}
function Donations(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <DonationFeedItem.ContextProvider><DonationsCombined {...props} /></DonationFeedItem.ContextProvider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export default Donations;